import {useRef} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {ImageSource, type ImageInput} from "@khan/guide-types";

import {
    CameraCapture,
    type CameraCaptureHandle,
} from "../camera-capture/camera-capture.tsx";
import {ImagePreview} from "../image-preview.tsx";

import {CameraCaptureActionButtons} from "./camera-capture-action-buttons.tsx";
import {PREVIEW_HEIGHT, PREVIEW_WIDTH} from "./constants.ts";
import {sharedStyles} from "./shared-styles.ts";

interface Props {
    // The currently captured image, or undefined if none.
    image: ImageInput | undefined;
    // Callback to set or clear the captured image.
    setImage: (image: ImageInput | undefined) => void;
}

/**
 * Renders the camera capture experience for the image attachment modal.
 * It displays a camera interface for capturing an image, or a preview of the captured image
 * with an option to retake. Used as a content component within the image attach modal wrapper.
 */
export const CameraCaptureModalContent = ({image, setImage}: Props) => {
    const cameraRef = useRef<CameraCaptureHandle>(null);
    return (
        <View style={sharedStyles.content}>
            <View style={sharedStyles.previewContainer}>
                {image ? (
                    <ImagePreview
                        displayHeight={PREVIEW_HEIGHT}
                        displayWidth={PREVIEW_WIDTH}
                        image={image.imageURL}
                    />
                ) : (
                    <CameraCapture
                        ref={cameraRef}
                        onCapture={(dataURL) => {
                            setImage({
                                imageURL: dataURL,
                                moderated: false,
                                source: ImageSource.ProvidedByCamera,
                            });
                        }}
                        disabled={!!image}
                        showCamera={true}
                        onCancel={() => {}}
                    />
                )}
            </View>
            <Strut size={spacing.large_24} />
            <CameraCaptureActionButtons
                hasImage={!!image}
                onRetake={() => setImage(undefined)}
                onCapture={() => cameraRef.current?.triggerCapture()}
                captureDisabled={!!image}
            />
        </View>
    );
};
